import { useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 60_000;

async function fetchDeployedVersion(): Promise<string | null> {
    try {
        const res = await fetch(`/deployment.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        const data = await res.json();
        return typeof data.version === 'string' ? data.version : null;
    } catch {
        return null;
    }
}

export default function useAppVersionCheck() {
    const baselineVersionRef = useRef<string | null>(null);
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let intervalId: ReturnType<typeof setInterval> | null = null;

        async function check() {
            const version = await fetchDeployedVersion();
            if (cancelled || version === null) return;

            if (baselineVersionRef.current === null) {
                baselineVersionRef.current = version;
                return;
            }

            if (version !== baselineVersionRef.current) {
                setIsUpdateAvailable(true);
                cleanup();
            }
        }

        function cleanup() {
            cancelled = true;
            if (intervalId !== null) {
                clearInterval(intervalId);
                intervalId = null;
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }

        function handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                check();
            }
        }

        check();
        intervalId = setInterval(check, POLL_INTERVAL_MS);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return cleanup;
    }, []);

    return { isUpdateAvailable };
}
